import React, {useCallback, useContext, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';


import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {openDrawer} from 'src/modules/entitlements/components/drawer/entitlement-drawer.slice';
import {EntitlementType} from 'src/modules/entitlements/components/drawer/typedef';
import {useExpandPresets} from 'src/modules/entitlements/hooks/useExpandPresets';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';
import {LocaleContext} from '@pb/kit-context-locale';
import {pluralize} from '@pb/kit-formatters';

import {ManageEntitlementsHeader} from '../../components/manage-entitlements-header';
import {MANAGE_ENTITLEMENTS_TABS, ManageEntitlementsTabs} from '../../components/manage-entitlements-tabs';
import {
    manageEntitlementsControlHeader,
    manageEntitlementsControlHeaderSticky,
    manageEntitlementsLoadingContainer,
} from '../../manage-entitlements.module.styl';

import {NoResultMessage} from './components/no-result-page';
import {PresetNameHeader} from './components/preset-name.header';
import {PresetSuggest} from './components/presets-suggest';
import {SECTION_NAME} from './constants';
import {useGetPresets} from './hooks/useGetPresets';
import {PresetsView} from './presets.view';
import {presetsStateActions} from './slices/presets-state.slice';
import {presetsActions} from './slices/presets.slice';

import type {AccessRight} from 'src/hooks/access-rights/constants';

export const Presets = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {locale} = useContext(LocaleContext);

    const {hasAccess} = useAdminAccessRight();
    const {result, loadingError, isLoading} = useGetPresets();
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const {
        isExpanded,
        disabled: isPresetExpansionDisabled,
        togglePresetsExpansion,
        expandedPresetsIds,
        setExpandedPresetsIds,
    } = useExpandPresets({records: result?.data.view || []});

    const navigateToPresetCreation = useCallback(() => {
        navigate('/entitlements/create-preset');
    }, [navigate]);

    const navigateToResourceCreation = useCallback(() => {
        navigate('/entitlements/create-resource');
    }, [navigate]);

    const navigateToGrantCreation = useCallback(() => {
        navigate('/entitlements/create-grant');
    }, [navigate]);

    const navigateToRoleCreation = useCallback(() => {
        navigate('/entitlements/create-role');
    }, [navigate]);

    const openNotAssociateEntitlementsDrawer = useCallback(() => {
        dispatch(openDrawer({
            entitlementType: EntitlementType.NOT_ASSOCIATED,
            entitlementId: '',
            notAssociatedEntitlementsDetails: {
                activeTab: 'roles',
                pagination: {currentPage: 1, pageSize: 10},
            },
        }));
    }, [dispatch]);

    useEffect(() => {
        return () => {
            dispatch(presetsActions.clearData());
            dispatch(presetsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <ServerResponseHandler
            serverError={loadingError}
            accessRight={ACCESS_RIGHTS.MANAGE_ENTITLEMENTS as AccessRight}
            sectionName={SECTION_NAME}>
            <>
                <ManageEntitlementsHeader
                    title="All Existing Entitlement Presets"
                    subtitle={result?.totalCount
                        ? pluralize(`${Integer.format(result.totalCount, locale)} Result[s]`, result.totalCount)
                        : undefined}
                    buttonsBlock={(
                        <Group>
                            {hasAccess(ACCESS_RIGHTS.DELETE_ENTITLEMENTS) ? (
                                <Button variant="Type.SECONDARY" onClick={openNotAssociateEntitlementsDrawer} colorVariant="ERROR">
                                    Delete
                                </Button>
                            ) : null}
                            <DropdownMenu
                                horizontalOffset={17}
                                verticalOffset={10}
                                anchor={({getAnchorProps, anchorRef, positionRef}) => (
                                    <Button
                                        ref={anchorRef}
                                        {...getAnchorProps()}
                                        variant="Type.TERTIARY"
                                        endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}>
                                        Create
                                    </Button>
                                )}>
                                <DropdownMenuItem
                                    caption="Resource"
                                    onClick={navigateToResourceCreation}
                                />
                                <DropdownMenuItem
                                    caption="Grant"
                                    onClick={navigateToGrantCreation}
                                />
                                <DropdownMenuItem
                                    caption="Role"
                                    onClick={navigateToRoleCreation}
                                />
                                <DropdownMenuItem
                                    caption="Preset"
                                    onClick={navigateToPresetCreation}
                                />
                            </DropdownMenu>
                        </Group>
                    )}
                />
                <ManageEntitlementsTabs activeTab={MANAGE_ENTITLEMENTS_TABS.PRESETS} disabled={isLoading}/>
                <StickyElement className={`manage-controls ${manageEntitlementsControlHeaderSticky}`}>
                    <div className={manageEntitlementsControlHeader}>
                        <Group justify="space-between" alignItems="center">
                            <PresetSuggest id="preset-search-suggest"/>
                            <Group>
                                <Label>Expand Presets</Label>
                                <StateButton
                                    {...{
                                        name: 'expand-presets',
                                        checked: isExpanded,
                                        disabled: isPresetExpansionDisabled,
                                        onChange: togglePresetsExpansion,
                                    }}
                                />
                            </Group>
                        </Group>
                    </div>
                </StickyElement>
                <BoxLoading loading={!result}>
                    <div className={manageEntitlementsLoadingContainer}>
                        {!!result?.data.view && (
                            <PresetsView
                                dataTableId="entitlement-presets"
                                expandedPresetsIds={expandedPresetsIds}
                                setExpandedPresetsIds={setExpandedPresetsIds}
                                rows={result.data.view}
                                relations={result.data.relations}
                                getColumnWidthByColumnId={getColumnWidthByColumnId}
                                onStopResize={onStopResize}
                                shortHeader={<PresetNameHeader/>}
                                totalCount={result.totalCount || 0}
                            />
                        )}
                        <NoResultMessage result={result}/>
                    </div>
                </BoxLoading>
            </>
        </ServerResponseHandler>
    );
};
